import { toast, type ToastOptions } from 'react-toastify';
import { RESEND_SUCCESS_MESSAGE_POPOVER_AUTOCLOSE_TIME } from '@/utils/constants/constants';
import { COLORS } from '@/utils/constants/colors';

const toToastMessage = (message: unknown): string => {
    if (message == null) return '';

    if (typeof message === 'string') {
        const trimmed = message.trim();
        if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
            try {
                return toToastMessage(JSON.parse(trimmed));
            } catch {
                return message;
            }
        }
        return message;
    }

    if (Array.isArray(message)) {
        return message.map((item) => toToastMessage(item)).filter(Boolean).join(', ');
    }

    if (typeof message === 'object') {
        const record = message as Record<string, unknown>;

        if ('response' in record) {
            const fromAxios = toToastMessage(
                (record.response as { data?: unknown } | undefined)?.data
            );
            if (fromAxios) return fromAxios;
        }

        if ('message' in record) {
            const nested = toToastMessage(record.message);
            if (nested) return nested;
        }

        if ('error' in record && typeof record.error === 'string') {
            return record.error;
        }
    }

    return '';
};

const baseToastOptions: ToastOptions = {
    position: 'top-center',
    autoClose: RESEND_SUCCESS_MESSAGE_POPOVER_AUTOCLOSE_TIME,
    hideProgressBar: true,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    className: 'toaster',
};

const showToast = (render: () => void) => {
    toast.dismiss();
    render();
};

export const errorToasterMessage = (message: unknown) =>
    showToast(() =>
        toast.error(toToastMessage(message) || 'Something went wrong', {
            ...baseToastOptions,
            icon: () => <em className="fs-4 icon ni ni-cross-circle-fill"></em>,
            style: {
                backgroundColor: 'rgb(255, 238, 233)',
                borderColor:
                    'rgba(209, 50, 57, 0.27) rgba(209, 50, 57, 0.27) rgba(209, 50, 57, 0.27) rgb(209, 50, 57)',
                color: 'rgb(178, 43, 49)',
            },
        })
    );

export const infoToasterMessage = (message: unknown) =>
    showToast(() =>
        toast.info(toToastMessage(message), {
            ...baseToastOptions,
            style: {
                backgroundColor: COLORS.infoBg,
                borderColor: COLORS.info,
                color: COLORS.info,
            },
        })
    );

export const successToasterMessage = (message: unknown) =>
    showToast(() =>
        toast.success(toToastMessage(message), {
            ...baseToastOptions,
            icon: () => <em className="fs-4 icon ni ni-check-circle-fill"></em>,
            style: {
                backgroundColor: COLORS.successTint,
                borderColor: COLORS.successAlt,
                color: COLORS.successAlt,
            },
        })
    );

export const warningToasterMessage = (message: unknown) =>
    showToast(() =>
        toast.warning(toToastMessage(message), {
            ...baseToastOptions,
            icon: () => <em className="fs-4 icon ni ni-alert-circle-fill"></em>,
            style: {
                backgroundColor: COLORS.warningTint,
                borderColor: 'rgb(255, 79, 0)',
                color: 'rgb(255, 79, 0)',
            },
        })
    );
